import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import CreateStory from './pages/CreateStory';
import Stories from './pages/Stories';
import StoryDetail from './pages/StoryDetail';
import Pricing from './pages/Pricing';
import Logo from './components/Logo';

// Landing page component
function Home() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Redirect to dashboard if already logged in
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background-cream">
      {/* Header Navigation */}
      <header className="bg-white shadow-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-end gap-4">
            <a
              href="/pricing"
              className="text-gray-600 hover:text-gray-900 font-semibold"
            >
              Pricing
            </a>
            <a
              href="/login"
              className="btn-outline px-6 py-2"
            >
              Login
            </a>
            <a
              href="/register"
              className="btn-primary px-6 py-2"
            >
              Sign Up
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="text-center">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <Logo size="xl" />
          </div>

          {/* Title */}
          <h1 className="text-6xl md:text-7xl font-display font-bold text-primary-400 mb-6">
            Story<span className="text-emerald-500">S</span>prout
          </h1>

          {/* Tagline */}
          <p className="text-2xl md:text-3xl text-text-dark font-semibold mb-4">
            Bespoke Audiobooks for Children
          </p>

          <p className="text-lg md:text-xl text-text-medium max-w-2xl mx-auto mb-12">
            Create magical, therapeutic audiobooks tailored to help your child navigate life's challenges, from
            first days at school to loss, illness, and beyond.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="/register"
              className="btn-primary px-8 py-4 text-lg"
            >
              Get Started Free
            </a>
            <a
              href="/login"
              className="btn-outline px-8 py-4 text-lg"
            >
              Login
            </a>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-4xl font-display font-bold text-center text-text-dark mb-12">
          How It Works
        </h2>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Step 1 */}
          <div className="card-hover text-center">
            <div className="text-5xl mb-4">📝</div>
            <h3 className="text-xl font-display font-bold text-emerald-500 mb-2">
              1. Share Your Story
            </h3>
            <p className="text-background-cream">
              Tell us about your child's situation, whether it's a new experience, challenge, or life change.
            </p>
          </div>

          {/* Step 2 */}
          <div className="card-hover text-center">
            <div className="text-5xl mb-4">✨</div>
            <h3 className="text-xl font-display font-bold text-emerald-500 mb-2">
              2. We Create Magic
            </h3>
            <p className="text-background-cream">
              We craft a personalized, therapeutic audiobook designed just for your child.
            </p>
          </div>

          {/* Step 3 */}
          <div className="card-hover text-center">
            <div className="text-5xl mb-4">🎧</div>
            <h3 className="text-xl font-display font-bold text-emerald-500 mb-2">
              3. Listen Together
            </h3>
            <p className="text-background-cream">
              Download and share the audiobook with your child, helping them feel understood and supported.
            </p>
          </div>
        </div>
      </div>

      {/* Use Cases */}
      <div className="bg-background-dark py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-display font-bold text-center text-text-dark mb-12">
            Stories for Every Moment
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { emoji: '🏫', label: 'First Day at School' },
              { emoji: '💔', label: 'Loss & Grief' },
              { emoji: '🏥', label: 'Medical Treatment' },
              { emoji: '😢', label: 'Bullying' },
              { emoji: '🏠', label: 'Moving House' },
              { emoji: '👶', label: 'New Sibling' },
              { emoji: '💑', label: 'Divorce' },
              { emoji: '😰', label: 'Anxiety & Fears' },
            ].map((useCase, index) => (
              <div
                key={index}
                className="p-6 border-2 border-primary-700 rounded-xl hover:bg-primary-900/30 transition-colors text-center bg-background-cream/50"
              >
                <div className="text-4xl mb-2">{useCase.emoji}</div>
                <p className="font-semibold text-text-dark">{useCase.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-primary-500 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg font-semibold mb-2">
            Built with ❤️ for children and families
          </p>
          <p className="text-sm opacity-80">
            COPPA & GDPR Compliant | Secure & Private
          </p>
        </div>
      </footer>
    </div>
  );
}

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);

  // Wait for store to rehydrate from localStorage before checking auth
  if (!hasHydrated) {
    return (
      <div className="min-h-screen bg-background-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-story"
          element={
            <ProtectedRoute>
              <CreateStory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stories"
          element={
            <ProtectedRoute>
              <Stories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stories/:storyId"
          element={
            <ProtectedRoute>
              <StoryDetail />
            </ProtectedRoute>
          }
        />
        {/* Fallback for undefined routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
